import { Product, ProductPricing, ServiceCategory } from '../types';

/** Minimal shape shared by Product and ProductSnapshot for pricing helpers. */
type PricingLike = { serviceCategory?: ServiceCategory; type?: Product['type']; pricing?: ProductPricing };

/** Resolve a product's service category, deriving from legacy `type` if unset. */
export function serviceCategoryOf(p: Pick<PricingLike, 'serviceCategory' | 'type'>): ServiceCategory {
  if (p.serviceCategory) return p.serviceCategory;
  switch (p.type) {
    case 'consultancy': return 'consulting';
    case 'development': return 'development';
    default: return 'managed_service'; // service, solution, undefined
  }
}

const PERIOD_WORD: Record<string, string> = { month: 'Monat', quarter: 'Quartal', year: 'Jahr', week: 'Woche' };

/** A selectable pricing building block that maps onto a single offer line item. */
export interface PricingComponent {
  /** Short chip label shown in the picker. */
  label: string;
  /** Default line-item description. */
  description: string;
  unitPrice: number;
  quantity: number;
  unit?: string;
  component: NonNullable<import('../types').OfferItem['pricingComponent']>;
}

/**
 * Derive the offer line-item building blocks a product offers, based on its
 * service category and structured pricing (rate cards, fixed packages,
 * recurring fee + setup). Falls back to the headline `amount` when no
 * structured pricing is present, so legacy products still produce one line.
 */
export function pricingComponentsFor(product: PricingLike): PricingComponent[] {
  const p = product.pricing;
  const out: PricingComponent[] = [];
  if (!p) return out;
  const cat = serviceCategoryOf(product);

  if (cat === 'managed_service') {
    if (p.amount && p.amount > 0) {
      const period = PERIOD_WORD[p.billingPeriod || 'month'];
      out.push({
        label: `Servicegebühr / ${period}`,
        description: `Wiederkehrende Servicegebühr (pro ${period})`,
        unitPrice: p.amount,
        quantity: 1,
        unit: period,
        component: 'recurring',
      });
    }
    if (p.setupFee && p.setupFee > 0) {
      out.push({
        label: 'Setup (einmalig)',
        description: 'Einmalige Setup-/Onboarding-Gebühr',
        unitPrice: p.setupFee,
        quantity: 1,
        unit: 'Einmalig',
        component: 'setup',
      });
    }
  } else {
    // consulting / development → rate cards (time & material)
    (p.rateCards || []).filter(r => r.dayRate > 0).forEach(r => {
      out.push({
        label: `${r.level} / Tag`,
        description: `${r.level} – Tagessatz`,
        unitPrice: r.dayRate,
        quantity: 1,
        unit: 'Tag(e)',
        component: 'day_rate',
      });
    });
    // development → optional fixed-price / turn-key packages
    (p.fixedPackages || []).forEach(pkg => {
      out.push({
        label: pkg.name || 'Paket',
        description: pkg.description ? `${pkg.name}: ${pkg.description}` : (pkg.name || 'Festpreis-Paket'),
        unitPrice: pkg.price,
        quantity: 1,
        unit: 'Pauschal',
        component: 'package',
      });
    });
  }

  // Fallback: nothing structured → use headline amount so a line can still be added.
  if (out.length === 0 && p.amount && p.amount > 0) {
    out.push({
      label: 'Festpreis',
      description: '',
      unitPrice: p.amount,
      quantity: 1,
      unit: 'Pauschal',
      component: 'fixed',
    });
  }

  return out;
}
